import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { HiOutlineArrowUpTray, HiMagnifyingGlass } from "react-icons/hi2";
import { ChevronDown } from "lucide-react";
import { fixMonthDisplay, getSlaName } from "../../Utils/utilServices";
import { PageHeader, Spinner } from "../ui/caliper";

const FILTER_INPUT =
  "h-8 px-2.5 border border-hairline rounded-cb-sm bg-panel text-t-md text-fg-default placeholder:text-fg-subtle outline-none focus:border-ink-500 focus:shadow-cb-focus transition-shadow";

const formatDateInput = (date) => {
  return date.toISOString().split("T")[0];
};

const EnquiryReport = () => {
  const today = new Date();
  const twoDaysAgo = new Date();
  twoDaysAgo.setDate(today.getDate() - 2);
  const user = JSON.parse(localStorage.getItem("loginObj"));
  const [loggedInUser, setloggedInUser] = useState(user.userRolesList.find(
    (user) => user.roleCode === "PHHEAD" || user.roleCode === "PH"
  ));
  const stateList = [
    { name: "Odisha", value: "OD" },
    { name: "Chhattisgarh", value: "CG" },
    { name: 'Jharkhand', value: 'JH' }
  ];
  const [defaultWHList, setdefaultWHList] = useState([]);
  const [warehouseList, setwarehouseList] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const warehouseDropdownRef = useRef(null);
  const [filter, setFilter] = useState({
    source: "CHE/Engage",
    enquiryId: "",
    patientId: "",
    orderId: "",
    fromDate: formatDateInput(twoDaysAgo),
    toDate: formatDateInput(today),
    state:
      loggedInUser?.roleCode != "PHHEAD"
        ? localStorage.getItem("userState")
        : "OD",
    circleCodes:
      loggedInUser?.roleCode === "PHHEAD"
        ? []
        : [localStorage.getItem("circleCode") || "N"],
  });

  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [dateRangeError, setDateRangeError] = useState(false);

  const itemsPerPage = 15;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const isB2B = filter.source === "B2B";

  const wareHouseData = async () => {
    try {
      const response = await axios.get(`${apiUrl}warehouse/list`);
      const res = response.data.successList;
      setdefaultWHList(res);
      setwarehouseList(res.filter((e) => e.stateCode == filter.state));
    } catch (err) {
      console.error("Error fetching warehouse details:", err);
    }
  };

  useEffect(() => {
    setwarehouseList(defaultWHList.filter((e) => e.stateCode == filter.state));
  }, [filter.state]);

  useEffect(() => {
    wareHouseData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        warehouseDropdownRef.current &&
        !warehouseDropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatDate = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const validateDateRange = (fromDate, toDate) => {
    const start = new Date(fromDate);
    const end = new Date(toDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 14;
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    // For date fields, validate the date range
    if (name === 'fromDate' || name === 'toDate') {
      const otherDateField = name === 'fromDate' ? 'toDate' : 'fromDate';
      const newDateRange = {
        ...filter,
        [name]: value,
      };

      const isValidRange = validateDateRange(
        name === 'fromDate' ? value : filter.fromDate,
        name === 'toDate' ? value : filter.toDate
      );

      setDateRangeError(!isValidRange);
    }

    setFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    if (dateRangeError) {
      return; // Prevent search if date range is invalid
    }
    fetchData();
  };

  const fetchData = async () => {
    setCurrentPage(1);
    setIsLoading(true);
    const baseUrl = isB2B
      ? `${apiUrl}report/enquiry/b2b`
      : `${apiUrl}report/enquiry`;

    try {
      const params = {
        state: filter.state,
        fromDate: formatDate(filter.fromDate),
        toDate: formatDate(filter.toDate),
      };

      if (filter.circleCodes && filter.circleCodes.length > 0) {
        params.circleCode = filter.circleCodes.join(",");
      }

      if (filter.enquiryId) {
        params.enquiryId = filter.enquiryId;
      }
      if (!isB2B && filter.patientId) {
        params.patientId = filter.patientId;
      }
      if (isB2B && filter.orderId) {
        params.orderId = filter.orderId;
      }

      const response = await axios.get(baseUrl, { params });

      if (response?.data?.successList) {
        setTableData(response.data.successList);
        setFilteredData(response.data.successList);
      } else {
        setTableData([]);
        setFilteredData([]);
      }
    } catch (error) {
      console.error("API Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    setIsLoading(true);
    let exportUrl = "";
    const baseParams = new URLSearchParams();
    baseParams.append("state", filter.state);
    baseParams.append("circleCode", filter.circleCode);
    baseParams.append("fromDate", formatDate(filter.fromDate));
    baseParams.append("toDate", formatDate(filter.toDate));

    if (filter.enquiryId) {
      baseParams.append("enquiryId", filter.enquiryId);
    }
    if (!isB2B && filter.patientId) {
      baseParams.append("patientId", filter.patientId);
    }
    if (isB2B && filter.orderId) {
      baseParams.append("orderId", filter.orderId);
    }

    if (isB2B) {
      exportUrl = `${apiUrl}export/download/enquiry/b2b?${baseParams.toString()}`;
    } else {
      exportUrl = `${apiUrl}export/download/enquiry?${baseParams.toString()}`;
    }

    try {
      const response = await axios.get(exportUrl, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.ms-excel",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `EnquiryReport_${filter.source}_${filter.fromDate}_to_${filter.toDate}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSourceChange = (e) => {
    setFilter((prev) => ({
      ...prev,
      source: e.target.value,
      enquiryId: "",
      patientId: "",
      orderId: "",
    }));
  };

  useEffect(() => {
    fetchData();
  }, [filter.source]);

  const renderPagination = () => {
    const visiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(visiblePages / 2));
    let endPage = startPage + visiblePages - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - visiblePages + 1);
    }

    const pageButtons = [];
    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`w-8 h-8 text-t-sm font-medium rounded-cb-sm border transition-colors ${currentPage === i
            ? "bg-ink-600 text-white border-ink-600"
            : "border-hairline text-fg-default hover:bg-surface-hover"
            }`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          className="text-lg disabled:opacity-50"
        >
          «
        </button>
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="text-lg disabled:opacity-50"
        >
          ‹
        </button>
        {pageButtons}
        <button
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          disabled={currentPage === totalPages}
          className="text-lg disabled:opacity-50"
        >
          ›
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          className="text-lg disabled:opacity-50"
        >
          »
        </button>
      </div>
    );
  };

  const headers = isB2B
    ? [
      "Enquiry ID",
      "Order ID",
      "Enquiry Code",
      "Partner ID",
      "Partner Name",
      "Partner DL Number",
      "Partner Address",
      "Drug Code",
      "Drug Name",
      "Package Size",
      "Quantity",
      "Free Quantity",
      "Total Quantity",
      "Unit Price (INR)",
      "Discount Percentage",
      "Total Amount (INR)",
      "Sales Person",
      "Enquiry Date",
      "Type Of Order",
      "Date & Time of Response",
      "Response Commited Drug Code",
      "Response Commited Drug Name",
      "Response Commited Quantity",
      "Response Free Quantity",
      "Response Total Quantity",
      "Response Commited Unit Price (INR)",
      "Response Commited Total Amount (INR)",
      "Response Commited Time",
      "Alternative Medicine",
    ]
    : [
      "Enquiry ID",
      "Enquiry Code",
      "Patient Name",
      "Patient ID",
      "Patient Address",
      "Drug Code",
      "Drug Name",
      "Package Size",
      "Quantity",
      "Unit Price (INR)",
      "Total Amount (INR)",
      "Eclinic Name",
      "Enquiry Date",
      "Type Of Order",
      "Date & Time of Response",
      "Response Committed Drug Code",
      "Response Committed Drug Name",
      "Response Committed Quantity",
      "Response Committed Unit Price (INR)",
      "Response Committed Total Amount (INR)",
      "Response Committed Time",
      "Alternative Medicine",
    ];

  return (
    <div className="flex flex-col gap-4 cb-fade-in">
      <PageHeader
        title={isB2B ? "B2B enquiry report" : "CHE / Engage enquiry report"}
        sub="Enquiry-to-response detail · exportable"
      />

      <div className="flex flex-wrap items-center gap-2 bg-panel border border-hairline rounded-cb-md px-3 py-2.5">
        <select
          className={`${FILTER_INPUT} w-44`}
          name="source"
          value={filter.source}
          onChange={handleSourceChange}
        >
          <option>CHE/Engage</option>
          {/* B2B Partners screens disabled — hide B2B source option. */}
          {/* <option>B2B</option> */}
        </select>
        {loggedInUser?.roleCode === "PHHEAD" && (
          <>
            <select
              value={filter.state}
              name="state"
              onChange={handleFilterChange}
              className={`${FILTER_INPUT} w-44`}
            >
              <option value="">Select state</option>
              {stateList.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.name}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <div className="relative w-60" ref={warehouseDropdownRef}>
                <div
                  className={`${FILTER_INPUT} flex items-center justify-between cursor-pointer`}
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  <span className="truncate">
                    {filter.circleCodes && filter.circleCodes.length > 0
                      ? `${filter.circleCodes.length} warehouse(s) selected`
                      : "Select warehouses"}
                  </span>
                  <ChevronDown
                    size={14}
                    strokeWidth={1.9}
                    className={`ml-2 text-fg-muted transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                  />
                </div>
                {dropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-panel border border-hairline rounded-cb-md shadow-cb-md max-h-60 overflow-y-auto cb-scroll py-1">
                    {warehouseList.map((warehouse) => {
                      const checked =
                        filter.circleCodes &&
                        filter.circleCodes.includes(warehouse.circleCode);
                      return (
                        <label
                          key={warehouse.pharmacyCode}
                          className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-surface-hover text-t-sm"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              let updated = [...(filter.circleCodes || [])];
                              if (e.target.checked) {
                                updated.push(warehouse.circleCode);
                              } else {
                                updated = updated.filter(
                                  (c) => c !== warehouse.circleCode
                                );
                              }
                              setFilter((prev) => ({
                                ...prev,
                                circleCodes: updated,
                              }));
                            }}
                            className="accent-ink-600"
                          />
                          {warehouse.pharmacyName}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              {filter.circleCodes && filter.circleCodes.length > 0 && (
                <button
                  onClick={() =>
                    setFilter((prev) => ({ ...prev, circleCodes: [] }))
                  }
                  className="text-red-700 text-t-xs font-semibold hover:underline whitespace-nowrap"
                >
                  Reset
                </button>
              )}
            </div>
          </>
        )}
        <input
          type="date"
          className={`${FILTER_INPUT} w-40 ${dateRangeError ? 'border-red-500' : ''}`}
          name="fromDate"
          value={filter.fromDate}
          onChange={handleFilterChange}
        />
        <input
          type="date"
          className={`${FILTER_INPUT} w-40 ${dateRangeError ? 'border-red-500' : ''}`}
          name="toDate"
          value={filter.toDate}
          onChange={handleFilterChange}
        />
        {dateRangeError && (
          <div className="text-red-700 text-t-xs w-full">
            Date range cannot exceed 2 weeks (14 days).
          </div>
        )}
        {!isB2B && (
          <input
            type="text"
            className={`${FILTER_INPUT} w-44`}
            placeholder="Patient ID"
            name="patientId"
            value={filter.patientId}
            onChange={handleFilterChange}
          />
        )}
        <input
          type="text"
          className={`${FILTER_INPUT} w-44`}
          placeholder="Enquiry ID"
          name="enquiryId"
          value={filter.enquiryId}
          onChange={handleFilterChange}
        />
        {isB2B && (
          <input
            type="text"
            className={`${FILTER_INPUT} w-44`}
            placeholder="Order ID"
            name="orderId"
            value={filter.orderId}
            onChange={handleFilterChange}
          />
        )}

        <button
          className={`inline-flex items-center gap-1.5 h-8 px-4 rounded-cb-sm text-t-md font-medium transition-colors ${dateRangeError
              ? 'bg-neutral-200 text-fg-subtle cursor-not-allowed'
              : 'bg-ink-600 text-fg-on-ink hover:bg-ink-700'
            }`}
          onClick={handleSearch}
          disabled={isLoading || dateRangeError}
        >
          <HiMagnifyingGlass className="h-4 w-4" />
          Search
        </button>

        <button
          className="inline-flex items-center gap-1.5 h-8 px-4 rounded-cb-sm border border-hairline bg-panel text-fg-strong text-t-md font-medium hover:bg-surface-hover transition-colors ml-auto"
          onClick={handleExport}
          disabled={isLoading}
        >
          <HiOutlineArrowUpTray className="h-4 w-4 text-fg-muted" />
          Export Excel
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center gap-2 h-64 text-fg-muted text-t-sm">
          <Spinner size={18} /> Loading report…
        </div>
      ) : (
        <>
          <div className="overflow-x-auto cb-scroll rounded-cb-md border border-hairline bg-panel">
            <table className="min-w-[1600px] text-t-sm text-fg-default border-collapse">
              <thead>
                <tr className="bg-surface-subtle border-b border-hairline">
                  {headers.map((header, idx) => (
                    <th key={idx} className="cb-th px-3 py-2.5 text-left whitespace-nowrap border-r border-hairline-subtle last:border-r-0">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((row, index) => (
                    <tr key={index} className="even:bg-surface-subtle hover:bg-surface-hover transition-colors border-b border-hairline-subtle">
                      {isB2B ? (
                        <>
                          <td className="px-4 py-2">{row.enquiryId}</td>
                          <td className="px-4 py-2">{row.orderId}</td>
                          <td className="px-4 py-2">{row.uniqueCode}</td>
                          <td className="px-4 py-2">{row.partnerId}</td>
                          <td className="px-4 py-2">{row.partnerName}</td>
                          <td className="px-4 py-2">{row.drugLicence}</td>
                          <td className="px-4 py-2 ">{row.address}</td>
                          <td className="px-4 py-2">{row.drugCode}</td>
                          <td className="px-4 py-2">{row.drugName}</td>
                          <td className="px-4 py-2">
                            {row.drugsInfoTypeOfSell}
                          </td>
                          <td className="px-4 py-2">{row.quantity}</td>
                          <td className="px-4 py-2">{row.freeQuantity}</td>
                          <td className="px-4 py-2">{row.totalQuantity}</td>
                          <td className="px-4 py-2">{row.unitPrice}</td>
                          <td className="px-4 py-2">{row.discountPercent}</td>
                          <td className="px-4 py-2">{row.totalAmount}</td>
                          <td className="px-4 py-2">{row.salesPerson}</td>
                          <td className="px-4 py-2">
                            {row.enquiryDate
                              ? fixMonthDisplay(
                                row.enquiryDate,
                                "MM/DD/YYYY hh:mm:ss"
                              )
                              : "-"}
                          </td>
                          <td className="px-4 py-2">{row.type}</td>
                          <td className="px-4 py-2">
                            {row.confirmedDate ? fixMonthDisplay(row.confirmedDate, "MM/DD/YYYY hh:mm:ss") : '-'}
                          </td>
                          <td className="px-4 py-2">{row.drugCode}</td>
                          <td className="px-4 py-2">{row.drugName}</td>
                          <td className="px-4 py-2">{row.alternateQuantity}</td>
                          <td className="px-4 py-2">
                            {row.alternateFreeQuantity}
                          </td>
                          <td className="px-4 py-2">
                            {row.alternateTotalQuantity}
                          </td>
                          <td className="px-4 py-2">
                            {row.alternateUnitPrice}
                          </td>
                          <td className="px-4 py-2">
                            {row.alternateTotalAmount}
                          </td>
                          <td className="px-4 py-2">
                            {getSlaName(row.timeslab)}
                          </td>
                          <td className="px-4 py-2">
                            {row.alternateMedicineName}
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-2">{row.enquiryId}</td>
                          <td className="px-4 py-2">{row.uniqueCode}</td>
                          <td className="px-4 py-2">{row.patientName}</td>
                          <td className="px-4 py-2">{row.patientId}</td>
                          <td className="px-4 py-2 ">{row.patientAddress}</td>
                          <td className="px-4 py-2">{row.drugCode}</td>
                          <td className="px-4 py-2">{row.drugName}</td>
                          <td className="px-4 py-2">
                            {row.drugsInfoTypeOfSell}
                          </td>
                          <td className="px-4 py-2">{row.quantity}</td>
                          <td className="px-4 py-2">{row.unitPrice}</td>
                          <td className="px-4 py-2">{row.totalAmount}</td>
                          <td className="px-4 py-2">{row.eclinicName}</td>
                          <td className="px-4 py-2">
                            {row.enquiryDate
                              ? fixMonthDisplay(
                                row.enquiryDate,
                                "MM/DD/YYYY hh:mm:ss"
                              )
                              : "-"}
                          </td>
                          <td className="px-4 py-2">{row.type}</td>
                          <td className="px-4 py-2">
                            {row.confirmedDate ? fixMonthDisplay(row.confirmedDate, "MM/DD/YYYY hh:mm:ss") : '-'}
                          </td>
                          <td className="px-4 py-2">{row.drugCode}</td>
                          <td className="px-4 py-2">{row.drugName}</td>
                          <td className="px-4 py-2">{row.alternateQuantity}</td>
                          <td className="px-4 py-2">
                            {row.alternateUnitPrice}
                          </td>
                          <td className="px-4 py-2">
                            {row.alternateTotalAmount}
                          </td>
                          <td className="px-4 py-2">
                            {getSlaName(row.timeslab)}
                          </td>
                          <td className="px-4 py-2">
                            {row.alternateMedicineName}
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={headers.length}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && renderPagination()}
        </>
      )}
    </div>
  );
};

export default EnquiryReport;
